import { nanoid } from 'nanoid';

// Preset salutation text. The smart {{greeting}} token is resolved by the backend greetingBuilder
// with postmailing defaults ("Sehr geehrter Herr Müller," / "Sehr geehrte Frau Klein,"). For
// company recipients without a contact person, the fallback is used automatically.
export const PM_SALUTATION_FORMAL = '{{greeting|fallback=Sehr geehrte Damen und Herren}},';

// Default attribute set for each supported block type.
export const PM_BLOCK_DEFAULTS = {
	heading: { type: 'heading', level: 2, text: 'Neue Überschrift', color: '#111', align: 'left' },
	paragraph: {
		type: 'paragraph',
		text: 'Klicke, um deinen Text zu bearbeiten. Hier hast du Platz, deine Geschichte zu erzählen, das Angebot zu erklären oder eine persönliche Botschaft zu formulieren.',
		align: 'left',
	},
	salutation: { type: 'salutation', tone: 'formal', text: PM_SALUTATION_FORMAL },
	bullets: {
		type: 'bullets',
		title: 'Ihre Vorteile',
		items: ['Erster Vorteil', 'Zweiter Vorteil', 'Dritter Vorteil'],
		colorMode: 'soft',
		marker: 'chevron',
	},
	image: { type: 'image', src: '', placeholder: 'Bild hier ablegen', ratio: '4:3', caption: '' },
	contact: {
		type: 'contact',
		iconColor: '',
		iconBg: '',
		rows: [
			{ icon: 'phone', label: 'Telefon', value: '+49 30 123 456' },
			{ icon: 'mail', label: 'E-Mail', value: 'info@firma.de' },
			{ icon: 'globe', label: 'Web', value: 'www.firma.de' },
		],
	},
	divider: { type: 'divider', color: 'primary', style: 'solid' },
	spacer: { type: 'spacer', height: 16 },
	// Generic container: a single nesting level of content blocks on a tinted
	// background. Children are created lazily by pmNewBlock at insert time, so the
	// default ships empty and the container shows its own "add block" affordance.
	container: {
		type: 'container',
		bg: 'soft',
		children: [],
	},
	senderLine: { type: 'senderLine', text: 'Hoffmann Hausrente · Kurfürstendamm 11, 10719 Berlin' },
	address: {
		type: 'address',
		// DIN 5008 business mail: {{firmenname}} above the person line, then street + city.
		// Empty-resolving lines (e.g. firmenname for a private recipient, or anrede/vorname/nachname
		// for a company-only recipient) are dropped by the renderer so the block stays clean.
		lines: ['{{firmenname}}', '{{anrede}} {{vorname}} {{nachname}}', '{{straße}}', '{{plz}} {{ort}}'],
	},
	date: { type: 'date', text: '{{ort_absender}}, {{datum}}', align: 'left' },

	// B2B Hero specific blocks
	contactBlock: {
		type: 'contactBlock',
		align: 'right',
		title: 'B2B Hero GmbH',
		lines: [
			'Sophienstraße 40',
			'38118 Braunschweig',
			'Tel. 05331 9327360',
			'info@b2b-hero.com',
			'www.b2b-hero.com',
		],
	},
	voucherBox: {
		type: 'voucherBox',
		bgColor: '#0e0e2b',
		textColor: '#ffffff',
		showTopline: true,
		showSubtitle: true,
		showCode: true,
		showFootnote: true,
		topline: 'Jetzt einlösen',
		bigNumber: '50',
		bigLabel: 'Kontakte',
		smallLabel: 'Gratis',
		subtitle: '+ kostenlose KI-Analyse Ihrer Leads',
		codeLabel: 'Ihr persönlicher Code',
		code: 'B2B-HERO-50',
		footnote: 'gültig: UNBEGRENZT · ohne Kreditkarte',
	},
	stepsList: {
		type: 'stepsList',
		title: 'So sichern Sie sich Ihr Guthaben:',
		numColor: '',
		numBg: '',
		items: [
			'Auf www.b2b-hero.com kostenlos registrieren',
			'Branche & Region wählen – die KI findet passende Firmen',
			'Code eingeben & 50 Kontakte gratis sichern',
		],
	},
	iconList: {
		type: 'iconList',
		iconColor: '',
		iconBg: '',
		items: [
			{ icon: 'sparkles', title: 'KI-Firmenrecherche', subtitle: 'Passende Firmen automatisch' },
			{ icon: 'user', title: 'Geprüfte Ansprechpartner', subtitle: 'E-Mail & Telefon der Entscheider' },
			{ icon: 'mail', title: 'E-Mail-Kampagnen', subtitle: 'Automatisiert mit KI-Texten' },
		],
	},
	callToAction: {
		type: 'callToAction',
		bgMode: 'accent',
		title: 'Jetzt kostenlos starten – 50 Kontakte gratis',
		subtitle: 'www.b2b-hero.com · Tel. 05331 9327360 · ohne Kreditkarte',
		borderColor: '',
		// Optional QR on the right. When showQr is on, the banner becomes two columns (text left,
		// QR right). At send time the QR encodes a per-recipient tracking URL; the editor shows the
		// raw qrUrl. Scans are tracked + shown in the Statistik tab (like the former qrCode block).
		showQr: false,
		qrUrl: 'https://www.b2b-hero.com',
	},
	// Standalone QR code for any zone (body, sidebar, footer). Same tracking as the CTA banner's QR:
	// the editor encodes the raw qrUrl, the send encodes a per-recipient tracking URL.
	qrCode: {
		type: 'qrCode',
		qrUrl: 'https://www.b2b-hero.com',
		title: 'Jetzt scannen',
		caption: 'Kamera öffnen und QR-Code scannen',
		size: 'medium',
		layout: 'stack',
	},
	personCard: {
		type: 'personCard',
		src: '',
		name: 'Vorname Nachname',
		role: 'Vorname Nachname · Vertrieb & Beratung',
		company: 'B2B Hero GmbH',
		nameColor: '',
		roleColor: '',
		avatarBg: '',
	},
	phoneCard: {
		type: 'phoneCard',
		align: 'center',
		showTitle: true,
		bgColor: '',
		textColor: '',
		accentColor: '',
		title: 'Lieber persönlich?',
		phone: '05331 9327360',
		note: 'Wir zeigen Ihnen B2B Hero in 30 Min. live – kostenlos & unverbindlich.',
	},
	starRating: {
		type: 'starRating',
		stars: 5,
		starColor: '',
		text: '1.000+ zufriedene Kunden',
	},
};

// QR code edge length in mm per `size` key. At send time the code encodes a ~220-character tracking
// URL (≈70-module matrix at level Q); 26mm keeps a module above ~0.33mm, the floor for a reliable
// phone scan of a printed code.
export const PM_QR_SIZES = {
	small: { mm: 26, label: 'Klein' },
	medium: { mm: 30, label: 'Mittel' },
	large: { mm: 36, label: 'Groß' },
};
export const PM_QR_SIZE_DEFAULT = 'medium';

// Categories for grouping the block catalog in the "+ Block hinzufügen" picker.
// Order here defines the section order shown to the user.
export const PM_BLOCK_CATEGORIES = [
	{ id: 'text', label: 'Text' },
	{ id: 'highlight', label: 'Hervorhebung' },
	{ id: 'lists', label: 'Listen & Schritte' },
	{ id: 'contact', label: 'Kontakt & Person' },
	{ id: 'layout', label: 'Layout & Medien' },
];

// Block catalog presented inside the "+ Block hinzufügen" picker.
// `category` maps each block to a PM_BLOCK_CATEGORIES id; order within a category
// follows the array order below.
export const PM_BLOCK_CATALOG = [
	{ type: 'heading', label: 'Überschrift', icon: 'heading', desc: 'Großer Titel', category: 'text' },
	{ type: 'paragraph', label: 'Textabsatz', icon: 'alignLeft', desc: 'Fließtext', category: 'text' },
	{ type: 'salutation', label: 'Anrede', icon: 'user', desc: 'Sehr geehrte/r …', category: 'text' },
	{ type: 'bullets', label: 'Aufzählung', icon: 'list', desc: 'Stichpunkt-Liste', category: 'text' },
	{
		type: 'voucherBox',
		label: 'Gutschein-Box',
		icon: 'sparkles',
		desc: 'Großer Aktions-Code',
		category: 'highlight',
	},
	{
		type: 'callToAction',
		label: 'CTA-Banner',
		icon: 'package',
		desc: 'Großer Aktions-Streifen',
		category: 'highlight',
	},
	{ type: 'qrCode', label: 'QR-Code', icon: 'qrCode', desc: 'Scanbarer Link mit Tracking', category: 'highlight' },
	{ type: 'starRating', label: 'Sternebewertung', icon: 'sparkles', desc: '★★★★★ + Text', category: 'highlight' },
	{ type: 'stepsList', label: 'Schritte', icon: 'list', desc: 'Nummerierte Anleitung', category: 'lists' },
	{ type: 'iconList', label: 'Icon-Features', icon: 'layers', desc: 'Icon + Titel + Untertitel', category: 'lists' },
	{ type: 'personCard', label: 'Personen-Karte', icon: 'user', desc: 'Avatar mit Signatur', category: 'contact' },
	{ type: 'phoneCard', label: 'Telefon-Karte', icon: 'phone', desc: 'Beratungs-Hinweis', category: 'contact' },
	{ type: 'contact', label: 'Kontakt-Card', icon: 'phone', desc: 'Telefon, E-Mail, Web', category: 'contact' },
	{ type: 'contactBlock', label: 'Adressblock', icon: 'building', desc: 'Mehrzeiliger Kontakt', category: 'contact' },
	{ type: 'container', label: 'Farbige Box', icon: 'package', desc: 'Container mit Hintergrund', category: 'layout' },
	{ type: 'image', label: 'Bild / Slot', icon: 'image', desc: 'Bild oder Platzhalter', category: 'layout' },
	{ type: 'divider', label: 'Trennlinie', icon: 'minus', desc: 'Horizontale Linie', category: 'layout' },
	{ type: 'spacer', label: 'Abstand', icon: 'expand', desc: 'Leerraum', category: 'layout' },
];

// Content block types allowed inside a `container`. Structural blocks
// (address, signature, contactBlock) and the container itself are excluded — they
// belong on the zone level, and a single nesting depth is intentional
// (no container-in-container). The logo lives in a dedicated page slot (page.logo), not as
// a block.
export const PM_CONTAINER_ALLOWED_CHILDREN = [
	'heading',
	'paragraph',
	'bullets',
	'image',
	'divider',
	'spacer',
	'iconList',
	'stepsList',
];

// Icon mapping for outline tree.
export const PM_BLOCK_ICON = {
	senderLine: 'info',
	address: 'mapPin',
	date: 'calendar',
	heading: 'heading',
	paragraph: 'alignLeft',
	salutation: 'user',
	bullets: 'list',
	container: 'package',
	image: 'image',
	contact: 'phone',
	divider: 'minus',
	spacer: 'expand',
	contactBlock: 'building',
	voucherBox: 'sparkles',
	stepsList: 'list',
	iconList: 'layers',
	callToAction: 'package',
	qrCode: 'qrCode',
	personCard: 'user',
	phoneCard: 'phone',
	starRating: 'sparkles',
};

// Type → German display name, derived from the picker catalog so the inspector
// header and the catalog never drift apart. Structural blocks that have no
// catalog entry (they live in fixed zones) get their names from pmBlockLabel.
const PM_BLOCK_TYPE_LABELS = PM_BLOCK_CATALOG.reduce((acc, b) => {
	acc[b.type] = b.label;
	return acc;
}, {});

export function pmBlockTypeLabel(type) {
	return PM_BLOCK_TYPE_LABELS[type] || pmBlockLabel({ type });
}

export function pmNewBlock(type, overrides = {}) {
	const base = PM_BLOCK_DEFAULTS[type];
	if (!base) throw new Error('Unknown block type: ' + type);
	return { id: 'b-' + nanoid(8), ...JSON.parse(JSON.stringify(base)), ...overrides };
}

export function pmBlockLabel(b) {
	const trunc = (s, n = 30) => (s || '').trim().slice(0, n) + ((s || '').length > n ? '…' : '');
	switch (b.type) {
		case 'senderLine':
			return 'Absenderzeile';
		case 'address':
			return 'Adresse';
		case 'date':
			return 'Datum';
		case 'heading':
			return trunc(b.text) || 'Überschrift';
		case 'paragraph':
			return trunc(b.text) || 'Absatz';
		case 'salutation':
			return 'Anrede';
		case 'bullets':
			return 'Aufzählung' + (b.title ? ': ' + trunc(b.title, 18) : '');
		case 'container':
			return `Farbige Box (${(b.children || []).length})`;
		case 'image':
			return 'Bild' + (b.ratio ? ` (${b.ratio})` : '');
		case 'contact':
			return 'Kontakt-Card';
		case 'divider':
			return 'Trennlinie';
		case 'spacer':
			return `Abstand (${b.height || 16}px)`;
		case 'contactBlock':
			return 'Adressblock';
		case 'voucherBox':
			return 'Gutschein';
		case 'stepsList':
			return `Schritte (${(b.items || []).length})`;
		case 'iconList':
			return `Icon-Features (${(b.items || []).length})`;
		case 'callToAction':
			return 'CTA-Banner';
		case 'qrCode':
			return 'QR-Code';
		case 'personCard':
			return 'Personen-Karte';
		case 'phoneCard':
			return 'Telefon-Karte';
		case 'starRating':
			return 'Sternebewertung';
		default:
			return b.type;
	}
}
